import {DestroyableInjector, inject, Injectable, Injector} from '@angular/core';
import {ComponentType} from '@angular/cdk/portal';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {NOTIFICATION_DIALOG_DEFAULT_CONFIG, NotificationDialog} from '@aviary/dialogs/notification-dialog/notification-dialog.types';
import {BaseDialog, LARGE_DIALOG_WIDTH, MEDIUM_DIALOG_WIDTH, SMALL_DIALOG_WIDTH} from '@aviary/dialogs/base-dialog/base-dialog.types';
import {CONFIRMATION_DIALOG_DEFAULT_CONFIG, ConfirmationDialog} from '@aviary/dialogs/confirmation-dialog/confirmation-dialog.types';
import {asArray} from '@aviary/store/common/typing.helpers';
import {FORM_DIALOG_DEFAULT_CONFIG, FormDialog} from '@aviary/dialogs/form-dialog/form-dialog.types';
import {PersistentFormDialog} from '@aviary/dialogs/persistent-form-dialog/persistent-form-dialog.types';
import {PersistentFormDialogController} from '@aviary/dialogs/persistent-form-dialog/persistent-form-dialog.controller';

@Injectable({providedIn: 'root'})
export class MxDialogService {

  private dialog: MatDialog = inject(MatDialog);
  private injector: Injector = inject(Injector);

  public openNotificationDialog<DataType, DialogComponent extends NotificationDialog<DataType>>(
    component: ComponentType<DialogComponent>, data: DataType
  ): MatDialogRef<DialogComponent, void> {
    return this.openDialog(component, data, NOTIFICATION_DIALOG_DEFAULT_CONFIG);
  }

  public openConfirmationDialog<DataType, ValueType, DialogComponent extends ConfirmationDialog<DataType, ValueType>>(
    component: ComponentType<DialogComponent>, data: DataType
  ): MatDialogRef<DialogComponent, ValueType> {
    return this.openDialog(component, data, CONFIRMATION_DIALOG_DEFAULT_CONFIG);
  }

  public openFormDialog<
    DataSupertype, DataType extends DataSupertype, ValueSupertype, ValueType extends ValueSupertype,
    DialogComponent extends FormDialog<DataSupertype, ValueSupertype>
  >(
    component: ComponentType<DialogComponent>, data: DataType
  ): MatDialogRef<DialogComponent, ValueType> {
    return this.openDialog(component, data, FORM_DIALOG_DEFAULT_CONFIG);
  }

  public openPersistentFormDialog<
    DataSupertype, DataType extends DataSupertype, ValueSupertype, ValueType extends ValueSupertype,
    DialogComponent extends PersistentFormDialog<DataSupertype, ValueSupertype>
  >(
    component: ComponentType<DialogComponent>, data: DataType
  ): { dialogRef: MatDialogRef<DialogComponent, void>; controller: PersistentFormDialogController<ValueType> } {
    /*
      The dialog and the openPersistentFormDialog() operator need to share the same controller.  The dialog uses it to report submissions, and the operator
      uses it to report pending and failure states.  We instantiate a new one so that it is unique for each instance of the dialog.
    */
    const controller: PersistentFormDialogController<ValueType> = new PersistentFormDialogController<ValueType>();

    // We create a child injector here for the controller...
    const injector: DestroyableInjector = Injector.create({
      providers: [{provide: PersistentFormDialogController, useValue: controller}],
      parent: this.injector
    });

    // ...and we supply that injector in the dialog config so that it's available to inject and use in the dialog component when we open it.
    const dialogRef: MatDialogRef<DialogComponent, void> = this.openDialog<DataSupertype, DataType, void, void, DialogComponent>(
      component,
      data,
      {...FORM_DIALOG_DEFAULT_CONFIG, injector}
    );

    // We return both the dialogRef and the same controller instance injected by the component so that the operator has access to both.
    return {dialogRef, controller};
  }

  /**
   * WHY "SUPERTYPES"?
   *
   * We often create multipurpose dialogs (like those that support create and edit activities).  These dialogs generally need wide union types for both their
   * dialog data and their returned values.  But effects are dealing with only one part of each of those wider types.  This used to require ugly (and
   * potentially dangerous) casting to convince TypeScript that we really meant to use the narrower type.
   *
   * With these supertypes, we can satisfy both the dialog component's wider type and the effect's narrower type at the same time.  And by declaring that
   * the narrow type extends the wider supertype, TypeScript understands the relationship.
   *
   * The result is that effects no longer need those inelegant casts.  Assuming the dialog is typed correctly, the effect is simple to write.  All that is
   * needed is a correctly typed argument in the onConfirm() implementation, and TypeScript can infer the rest.
   *
   * And for simpler, single-purpose form dialogs?  In those cases, the supertype just happens to be the same as the narrower type, and it all still works.
   *
   * For a usage example, see PlaceEditHoldDialog and HoldsEffects.showPlaceHoldForCatalogRecord.
   */
  protected openDialog<
    DataSupertype, DataType extends DataSupertype, ValueSupertype, ValueType extends ValueSupertype,
    DialogComponent extends BaseDialog<DataSupertype, ValueSupertype>
  >(
    component: ComponentType<DialogComponent>, data: DataType, defaultConfig: MatDialogConfig
  ): MatDialogRef<DialogComponent, ValueType> {
    const config: MatDialogConfig<DataType> = this.createDialogConfigFrom<DataSupertype, DataType, DialogComponent>(component, data, defaultConfig);
    const dialogRef: MatDialogRef<DialogComponent, ValueType> = this.dialog.open<DialogComponent, DataType, ValueType>(component, config);

    this.defineDialogCloserIfAppropriateFor(dialogRef);

    return dialogRef;
  }

  private createDialogConfigFrom<
    DataSupertype, DataType extends DataSupertype,
    DialogComponent extends BaseDialog<DataSupertype, unknown>
  >(
    component: ComponentType<DialogComponent>, data: DataType, defaultConfig: MatDialogConfig
  ): MatDialogConfig<DataType> {
    const overrideConfigFromComponent: MatDialogConfig = component['dialogConfigOverrides'] ?? {};

    const computedConfig: MatDialogConfig<DataType> = {
      ...defaultConfig,
      ...overrideConfigFromComponent,
      data
    };

    return {
      ...computedConfig,
      panelClass: [
        ...asArray(computedConfig.panelClass),
        this.determineSizePanelClassFor(computedConfig)
      ].flat()
    };
  }

  private determineSizePanelClassFor(config: MatDialogConfig): string {
    switch (config.maxWidth) {
      case SMALL_DIALOG_WIDTH:
        return 'hk-small-dialog';
      case MEDIUM_DIALOG_WIDTH:
        return 'hk-medium-dialog';
      case LARGE_DIALOG_WIDTH:
        return 'hk-large-dialog';
      default:
        return '';
    }
  }

  public defineDialogCloserIfAppropriateFor<DialogComponent, ValueType>(dialogRef: MatDialogRef<DialogComponent, ValueType>): void {
    const componentInstance: DialogComponent = dialogRef.componentInstance;

    if ('closeDialog' in (componentInstance as object)) componentInstance['closeDialog'] = (result?: ValueType) => dialogRef.close(result);
  }
}
